import { useCallback, useEffect, useRef, useState } from 'react';

/** A section becomes current once its top passes this share of the window's height. */
const READING_LINE = 0.3;
/** How long the page must stay still before a jump from the contents counts as finished. */
const SETTLE_MS = 150;
/** A picked section that needs no scrolling stops being held after this long. */
const HOLD_MS = 1000;

/**
 * The section being read, for the contents of a long page: the last one whose top has passed
 * the reading line, or the last one at the very end of the page, where short final sections can
 * never reach the line. A section picked in the contents (or in the address) is current at once
 * and stays so while the page scrolls to it, instead of the ones it passes on the way.
 */
export function useCurrentSection(ids: readonly string[]) {
  const [current, setCurrent] = useState<string | null>(null);
  const held = useRef<string | null>(null);
  const release = useRef(0);

  const hold = useCallback((id: string, ms: number) => {
    held.current = id;
    window.clearTimeout(release.current);
    release.current = window.setTimeout(() => {
      held.current = null;
    }, ms);
  }, []);

  const pick = useCallback(
    (id: string) => {
      setCurrent(id);
      hold(id, HOLD_MS);
    },
    [hold],
  );

  useEffect(() => {
    const sections = ids.flatMap((id) => {
      const element = document.getElementById(id);
      return element === null ? [] : [element];
    });
    const first = sections[0];
    const last = sections.at(-1);
    if (first === undefined || last === undefined) return;
    let frame = 0;

    const measure = () => {
      frame = 0;
      const line = window.innerHeight * READING_LINE;
      const atEnd =
        window.scrollY + window.innerHeight >= document.documentElement.scrollHeight - 2;
      let found = first.id;
      for (const section of sections) {
        if (section.getBoundingClientRect().top <= line) found = section.id;
      }
      setCurrent(atEnd ? last.id : found);
    };
    const onScroll = () => {
      if (held.current !== null) {
        hold(held.current, SETTLE_MS); // still on the way to the picked section
        return;
      }
      if (frame === 0) frame = window.requestAnimationFrame(measure);
    };

    // First look once the page has its layout (and the browser has jumped to an address's #id).
    frame = window.requestAnimationFrame(() => {
      const fromAddress = decodeURIComponent(window.location.hash.slice(1));
      if (sections.some((section) => section.id === fromAddress)) {
        frame = 0;
        pick(fromAddress);
      } else {
        measure();
      }
    });
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(release.current);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [ids, hold, pick]);

  return { current, pick };
}
